<div class="hidewhenloading course-page" #hidewhenloading>

  <!-- Completion banner. Was an absolutely-positioned badge floating over the top-right
       corner of the page; it is a proper banner now so it cannot overlap anything. -->
  <section class="course-result" *ngIf="this.moduleCompleted"
           [ngClass]="passed ? 'course-result--passed' : 'course-result--failed'">
    <div class="progress mb-0" [ngClass]="passed ? '' : 'progress-color'" [attr.data-value]="scorePercentage">
      <span class="progress-left"><span class="progress-bar" [ngClass]="passed ? 'border-primary' : 'border-failed'"></span></span>
      <span class="progress-right"><span class="progress-bar" [ngClass]="passed ? 'border-primary' : 'border-failed'"></span></span>
      <div class="progress-value w-100 h-100 rounded-circle d-flex align-items-center justify-content-center pt-2">
        <div class="h2 font-weight-bold mb-0">{{scorePercentage}}%</div>
      </div>
    </div>
    <div class="course-result__text">
      <h2 class="course-result__title mb-1">{{passed ? 'Course passed' : 'Course not passed'}}</h2>
      <p class="mb-0">You scored {{scorePercentage}}% on this course.</p>
    </div>
  </section>

  <!-- Hero: teal band, details left, banner right; runs to the page edges. -->
  <ng-container *ngTemplateOutlet="courseHero; context: { variant: 'teal', primary: true }"></ng-container>

  <ng-template #courseHero let-variant="variant" let-primary="primary">
  <section class="course-hero course-hero--{{variant}}">
    <div class="course-hero__main">
      <span class="course-hero__code" *ngIf="moduleInfo.courseCode">{{moduleInfo.courseCode}}</span>
      <h1 class="course-hero__title">{{moduleInfo.name}}</h1>

      <ul class="course-hero__meta">
        <li *ngIf="lessonCount > 0"><i class="fas fa-book-open" aria-hidden="true"></i> {{lessonCount}} Lesson{{lessonCount === 1 ? '' : 's'}}</li>
        <li *ngIf="totalCourseDuration"><i class="far fa-clock" aria-hidden="true"></i> {{totalCourseDuration}} total</li>
        <li *ngIf="moduleInfo.accessdays"><i class="fas fa-calendar-alt" aria-hidden="true"></i> {{moduleInfo.accessdays}} Day{{moduleInfo.accessdays === 1 ? '' : 's'}} Access</li>
        <li *ngIf="!moduleInfo.accessdays"><i class="fas fa-calendar-alt" aria-hidden="true"></i> Unlimited Access</li>
      </ul>

      <p class="course-hero__warning" *ngIf="!moduleInfo.nextBatchDate && moduleInfo.allowBatches && currentUserIsAdmin">
        <i class="fa fa-info-circle"></i> Users will not be able to enroll until a new batch has been created
      </p>

      <div class="course-hero__panel">
      <div class="course-hero__price" *ngIf="showPriceBlock">
        <span class="course-hero__amount">{{moduleInfo.price ? (this.currency + this.formatNumber(moduleInfo.price)) : 'Free'}}</span>
        <!-- id only on the first copy: the guided tour targets #moduleaccessdays -->
        <span class="course-hero__accessnote" [attr.id]="primary ? 'moduleaccessdays' : null">{{moduleInfo.accessdays || 'Unlimited'}} day{{moduleInfo.accessdays === 1 ? '' : 's'}} access</span>
      </div>

      <p class="course-hero__batch" *ngIf="moduleInfo.nextBatchDate">
        <b>Next batch starts:</b> {{this.util.formatDate(moduleInfo.nextBatchDate)}}
      </p>

      <div class="course-hero__actions">
        <!-- Enrolled student: straight into the lessons -->
        <!-- Goes to the first unfinished lesson, not just to the curriculum tab. -->
        <button *ngIf="currentUserIsStudent" class="btn btn-primary m-0"
                [disabled]="!canViewLessons || !nextLesson"
                (click)="startCourse()">
          <i class="fas fa-play-circle" aria-hidden="true"></i> {{startCourseLabel}}
        </button>

        <!-- Signed in, not enrolled. Unchanged condition and unchanged handler: enrolment
             and payment are M14 and are not touched by this redesign. -->
        <button *ngIf="moduleInfo.requireEnrollment && !currentUserIsAdmin && !currentUserIsStudent && !notauser"
                [disabled]="!moduleInfo.nextBatchDate && moduleInfo.allowBatches || isFetchingData"
                (click)="submtEnroll()" class="btn btn-primary m-0">{{ enrollText }}</button>

        <!-- Signed out -->
        <a *ngIf="!useraccessservice.loggedIn()" [routerLink]="['/access/register']"
           [queryParams]="{ goto: '/user/moduledetails?moduleID=' + moduleInfo.id }">
          <button class="btn btn-primary m-0">Enroll now</button>
        </a>

        <a *ngIf="canSeeForum" routerLink="/user/forum" [queryParams]="{ 'moduleid': this.moduleInfo.id }">
          <button class="btn m-0"><i class="far fa-comments" aria-hidden="true"></i> Go to Course Forum</button>
        </a>

        <button *ngIf="this.courseEvent.length > 0 && currentUserIsStudent" (click)="gotoUpcomingEvents()" class="btn m-0">
          <i class="fas fa-eye" aria-hidden="true"></i> View Course Events
        </button>
      </div>
        <p class="course-hero__reason" *ngIf="actionDisabledReason">
          <i class="fas fa-info-circle" aria-hidden="true"></i> {{actionDisabledReason}}
        </p>
      </div>
    </div>

    <div class="course-hero__media">
      <img src="{{modulelogourl}}" alt="Banner image for {{moduleInfo.name}}">
      <span class="course-hero__badge" *ngIf="showPriceBlock">{{moduleInfo.price ? (this.currency + this.formatNumber(moduleInfo.price)) : 'FREE'}}</span>
    </div>
  </section>
  </ng-template>

  <!-- Tabs. The forum is a separate page, so it stays a link rather than a tab. -->
  <nav class="course-tabs" role="tablist">
    <button role="tab" [attr.aria-selected]="activeTab === 'overview'"
            [class.is-active]="activeTab === 'overview'" (click)="activeTab = 'overview'">Overview</button>
    <button role="tab" [attr.aria-selected]="activeTab === 'curriculum'"
            [class.is-active]="activeTab === 'curriculum'" (click)="activeTab = 'curriculum'">Curriculum</button>
    <button role="tab" [attr.aria-selected]="activeTab === 'milestone'"
            [class.is-active]="activeTab === 'milestone'" (click)="activeTab = 'milestone'"
            *ngIf="lessons?.length > 1 && !currentUserIsAdmin">Milestone</button>
  </nav>

  <div class="course-body">
    <div class="course-body__main">

      <!-- OVERVIEW -->
      <ng-container *ngIf="activeTab === 'overview'">
        <section class="course-welcome" *ngIf="currentUserIsStudent && currentUserFirstName">
          <span class="course-welcome__label">Welcome to your course</span>
          <h3 class="course-welcome__heading">Hey {{currentUserFirstName}}, you're in! &#10024;</h3>
          <p class="course-welcome__text">Your learning journey starts here. Explore, learn, and make every lesson count.</p>
        </section>

        <section class="course-card">
          <h2 class="course-card__title">About this course</h2>
          <div id="coursedescription" [innerHTML]="moduledescription" class="innerHTML-wrap"></div>

          <div class="course-packages" *ngIf="modulePackageNames.length > 0">
            <h3 class="course-card__subtitle">This course is part of package(s)</h3>
            <div class="list-group list-group-flush rounded-md shadow-sm-darker overflow-hidden">
              <a *ngFor="let modulePackageName of modulePackageNames" routerLink="/user/packagedetails"
                 [queryParams]="{ 'packageId': modulePackageName.id}"
                 class="list-group-item list-group-item-action text-sm text-primary-darker">{{modulePackageName.name}}</a>
            </div>
          </div>
        </section>
      </ng-container>

      <!-- CURRICULUM -->
      <section class="course-card" *ngIf="activeTab === 'curriculum'">
        <div class="course-card__head">
          <div>
            <h2 class="course-card__title mb-1">Course Curriculum</h2>
            <p class="course-card__meta mb-0">
              {{lessonCount}} lesson{{lessonCount === 1 ? '' : 's'}}<span *ngIf="totalCourseDuration"> &middot; {{totalCourseDuration}} total duration</span>
            </p>
          </div>
        </div>

        <div *ngIf="(!lessons || lessons.length === 0) && currentUserIsAdmin" class="bg-white p-4 rounded shadow-sm-darker">
          <button routerLink='/user/editlesson' [queryParams]="lessonurlparam" class="btn">
            <i class="fa fa-plus"></i> Create Your First Lesson
          </button>
        </div>

        <app-classlist [lessons]="lessons" [admin]=currentUserIsAdmin
          [canViewLessons]="canViewLessons" [savedLessonIds]="savedLessonIds"
          [offlineDegraded]="offlineDegraded"></app-classlist>
      </section>

      <!-- MILESTONE -->
      <section class="course-card" *ngIf="activeTab === 'milestone'">
        <h2 class="course-card__title mb-1">Milestone</h2>
        <app-milestone
          [activateLessonDeadlineNotification]="moduleInfo.activateLessonDeadlineNotification"
          [lessons]="lessons"
          [enrolledDate]="enrolledDate">
        </app-milestone>
      </section>
    </div>

    <aside class="course-body__side">
      <section class="course-card course-side-card" *ngIf="currentUserIsStudent">
        <h3 class="course-card__subtitle">Your Progress</h3>
        <div class="progress mx-auto mb-2" [attr.data-value]="progress">
          <span class="progress-left"><span class="progress-bar border-primary"></span></span>
          <span class="progress-right"><span class="progress-bar border-primary"></span></span>
          <div class="progress-value w-100 h-100 rounded-circle d-flex align-items-center justify-content-center pt-2">
            <div class="h2 font-weight-bold mb-0">{{progress}}%</div>
          </div>
        </div>
        <p class="text-center mb-0">completed</p>

        <button class="btn mt-3 w-100" *ngIf="this.showCompleteButton && !this.moduleCompleted"
                [disabled]="!this.enableCompleteButton" (click)="markCourseComplete()">Complete Course</button>
      </section>

      <section class="course-card course-side-card" *ngIf="canSeeForum">
        <h3 class="course-card__subtitle">Course Community</h3>
        <p class="course-card__meta">Discuss with students, ask questions and share resources.</p>
        <a routerLink="/user/forum" [queryParams]="{ 'moduleid': this.moduleInfo.id }">
          <button class="btn w-100 m-0">Go to Course Forum</button>
        </a>
      </section>

      <section class="course-card course-side-card course-more" *ngIf="otherCourses.length > 0">
        <h3 class="course-card__subtitle">More courses</h3>
        <a *ngFor="let course of otherCourses" class="course-more__item" [href]="otherCourseHref(course)">
          <span class="course-more__thumb">
            <img [src]="course.imageUrl" alt="" loading="lazy">
          </span>
          <span class="course-more__text">
            <span class="course-more__title">{{course.courseTitle}}</span>
            <span class="course-more__meta">
              {{course.lessonCount}} lesson{{course.lessonCount === 1 ? '' : 's'}} &middot;
              {{course.price ? (this.currency + this.formatNumber(course.price)) : 'Free'}}
            </span>
          </span>
          <i class="fas fa-chevron-right course-more__arrow" aria-hidden="true"></i>
        </a>
        <a routerLink="/site/courses" class="course-more__all">Browse all courses <i class="fas fa-arrow-right" aria-hidden="true"></i></a>
      </section>

      <section class="course-card course-side-card">
        <h3 class="course-card__subtitle">Your Tutors</h3>
        <p *ngIf="tutorList.length === 0" class="course-card__meta mb-0">No tutor has been added to this course yet.</p>
        <app-adminusers [school]=true [tutorList]="tutorList" [moduledisplay]=true [showbuttons]=false></app-adminusers>
      </section>
    </aside>
  </div>
</div>

<div class="modal fade" id="deleteCourse" data-keyboard="false" data-backdrop="static" aria-hidden="true" role="dialog">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="modal-title">Delete Course</h3>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body pt-0">
        <p class="text-center pb-2">Are you sure you want to delete this course?</p>
        <div class="d-flex justify-content-center flex-wrap gap-1.5">
          <button type="submit" class="btn btn--danger m-0" (click)="deleteModule()">Delete</button>
          <button type="button" class="btn m-0" data-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="modal fade" id="addStudentModal" tabindex="-1" role="dialog" aria-labelledby="addStudentModalTitle" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="modal-title" id="addStudentModalTitle">Add a School Student</h3>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <app-addstudentusers *ngIf="showAddStudentModal" [moduleId]="moduleInfo.id" [embedded]="true"
          (studentAdded)="onStudentAdded()"></app-addstudentusers>
      </div>
    </div>
  </div>
</div>

<app-paymentiframe #payment [loadingPayment]="loadingPayment"></app-paymentiframe>

<div class="modal fade" #enrolstudents id="enrolstudents" aria-hidden="true" aria-labelledby="enrolstudents"
  role="dialog">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="modal-title">Enrol Students for Course</h3>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <p *ngIf="!allowBatches"><a href="../../assets/templates/CourseStudentEnrollTemplate.xlsx"
            class="text-link text-xs"><i class="fa fa-info-circle"></i> Download Template</a></p>
        <p *ngIf="allowBatches"><a href="../../assets/templates/CourseStudentEnrollTemplateBatched.xlsx"
            class="text-link text-xs"><i class="fa fa-info-circle"></i> Download Template</a></p>
        <form #uploadform="ngForm" (ngSubmit)="enrolstudent(files.files)" name="uploadform">
          <p class="text-center text-sm">{{enrolFileName || 'No File Selected'}}</p>
          <button (click)="files.click()" type="button" id="filesLabel" class="btn m-auto">Select
            File</button>
          <input #files (change)="enrolFileName = files.files[0]?.name" type="file" name="files" accept=".xls,.xlsx"
            aria-labelledby="filesLabel" id="classfile" class="sr-only" required>
          <div class="d-flex justify-content-around flex-wrap gap-1.5 mt-5">
            <button type="submit" class="btn m-0" [disabled]="files.files.length < 1">Upload</button>
            <button type="button" class="btn btn--warning m-0" (click)="closeEnrolModal()">Close</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>

<div class="modal fade" id="upcomingEventModal" data-keyboard="false" data-backdrop="static" aria-hidden="true"
  role="dialog">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="modal-title">Upcoming Event</h3>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body pt-0">
        <div class="table-responsive">
          <table class="table align-items-center table-flush table-hover">
            <!-- <thead>
                    <tr>
                        <th colspan="4" class="text-right"> Total : </th><th class="text-right">₦{{this.util.numberWithCommas(sumTotal)}}</th>
                    </tr>
                </thead> -->
            <thead class="thead-light">
              <tr>
                <th> Subject </th>
                <th class="text-center"> Status </th>
                <th class="text-center"> Start Date</th>
                <th> End Date</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of courseEvent">
                <td>{{item.subject}}</td>
                <td
                  [ngClass]="{ 'bg-red': item.isCancelled, 'bg-green': !item.isCancelled && !isExpierd(item),'bg-yellow': !item.isCancelled && isExpierd(item) }">
                  {{ item.isCancelled ?
                  'Cancelled' : isExpierd(item)? 'Expired':'Active'}}</td>
                <td class="text-center">{{this.util.formatDate(item.inviteStartDate)}}</td>
                <td>{{this.util.formatDate(item.inviteEndDate)}}</td>
              </tr>
            </tbody>
          </table>
          <p *ngIf="courseEvent.length<=0" class="text-center">No Available event</p>
          <div class="d-flex justify-content-center flex-wrap gap-1.5">
            <button type="button" class="btn m-0" data-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>


<div class="modal fade" id="popbatch" data-keyboard="false" data-backdrop="static" aria-hidden="true" role="dialog">
  <div class="modal-dialog" style="    max-width: max-content !important;">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="modal-title">Select Batch</h3>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body pt-0">
        <div class="d-flex justify-content-center mb-3">
          <select class="form-control" [(ngModel)]="selectedBatchId" [ngModelOptions]="{standalone: true}">
            <option selected disabled value="0">Select Batch</option>           
            <option *ngFor="let item of moduleBatches" [ngValue]="item.id">
              {{ item.name }} | Start Date: {{ util.formatDate(item.startDate) }}
            </option>
          </select>
        </div>

        <div class="d-flex justify-content-center flex-wrap gap-1.5">
          <button type="submit" class="btn btn--primary m-0" (click)="enroll()">Submit</button>
          <button type="button" class="btn btn--warning m-0" data-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="modal fade" #scromupload id="scromupload" aria-hidden="true" aria-labelledby="scromupload" role="dialog">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="modal-title">Upload Scrom File (v1.2)</h3>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <form #uploadform="ngForm" (ngSubmit)="uploadScrom(Scormfiles.files)" name="uploadform">
          <p class="text-center text-sm">{{scromFileName || 'No File Selected'}}</p>
          <button (click)="Scormfiles.click()" type="button" id="filesLabel" class="btn m-auto">Select
            File</button>
          <input #Scormfiles (change)="scromFileName = Scormfiles.files[0]?.name" type="file" name="Scormfiles"
            accept=".zip" aria-labelledby="filesLabel" id="classfile" class="sr-only" required>
          <div class="d-flex justify-content-around flex-wrap gap-1.5 mt-5">
            <button type="submit" class="btn m-0" [disabled]="Scormfiles.files.length < 1">Upload</button>
            <button type="button" class="btn btn--warning m-0" data-dismiss="modal" aria-label="Close">Close</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>


<div class="modal fade" #welcometextfrominvitemail id="welcometextfrominvitemail" aria-hidden="true" aria-labelledby="welcometextfrominvitemail" role="dialog">
  <div class="modal-dialog">
    <div class="modal-content">
      
      <div class="d-flex align-items-center w-100 p-3">      
        <h1 class="modal-title flex-grow-1 text-center m-0">
          {{ moduleInfo.price > 0 ? ('Complete your enrolment in ' + moduleInfo.name) : ('You are enrolled in ' + moduleInfo.name ) }}
        </h1>
      
        <button type="button" class="close ms-auto" data-dismiss="modal" aria-label="Close" style="font-size: x-large;">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <div class="success-icon text-center">
          <i class="fa fa-check-circle"></i>
        </div>
        <p class="start-date text-center">
          <ng-container *ngIf="moduleInfo.startDate; else noDate">
            It starts on {{ moduleInfo.startDate | date:'EEEE, MMM d, y' }}
          </ng-container>
          <ng-template #noDate>
            Available now — start any time.
          </ng-template>
        </p>
        <button class="btn btn-primary go-btn mt-5" data-dismiss="modal" (click)="payFromModalPopUp()">
          {{ moduleInfo.price > 0 ? ('Pay ₦' + moduleInfo.price + ' to enroll') : 'Go to my course' }}
        </button>
      </div>
    </div>
  </div>
</div>
